/**
 * Utility functions for DataTable component
 */
import { isValidElement, ReactNode } from "react";
import { DataTableColumn } from "./types";

const EXPORT_OBJECT_KEYS = [
  "companyName",
  "vendorName",
  "clientName",
  "fullName",
  "displayName",
  "name",
  "title",
  "label",
  "email",
  "value",
  "id",
];

const escapeCsvCell = (value: string): string => {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }

  return value;
};

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const extractTextFromReactNode = (node: ReactNode): string => {
  if (node === null || node === undefined || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) {
    return node.map((child) => extractTextFromReactNode(child)).filter(Boolean).join(" ");
  }
  if (isValidElement(node)) {
    const children = (node.props as { children?: ReactNode } | undefined)?.children;
    return extractTextFromReactNode(children);
  }
  return "";
};

const normalizeExportValue = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) {
    return value.map((item) => normalizeExportValue(item)).filter(Boolean).join("; ");
  }
  if (isValidElement(value)) {
    return extractTextFromReactNode(value);
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    for (const key of EXPORT_OBJECT_KEYS) {
      const candidate = record[key];
      if (candidate !== undefined && candidate !== null && candidate !== "") {
        return normalizeExportValue(candidate);
      }
    }

    const primitiveEntries = Object.entries(record)
      .filter(([, entryValue]) => ["string", "number", "boolean"].includes(typeof entryValue))
      .map(([key, entryValue]) => `${key}: ${normalizeExportValue(entryValue)}`);

    if (primitiveEntries.length > 0) {
      return primitiveEntries.join(" | ");
    }

    return JSON.stringify(record);
  }

  return String(value);
};

const getExportColumns = <T extends Record<string, unknown>>(columns: DataTableColumn<T>[]) =>
  columns.filter((column) => column.key !== "actions" && column.exportable !== false);

const getExportValue = <T extends Record<string, unknown>>(record: T, column: DataTableColumn<T>, index: number) => {
  const rawValue = getNestedValue(record, column.dataIndex || column.key);

  if (column.exportValue) {
    return normalizeExportValue(column.exportValue(rawValue, record, index));
  }

  if (rawValue !== undefined && rawValue !== null && rawValue !== "") {
    return normalizeExportValue(rawValue);
  }

  if (column.render) {
    return normalizeExportValue(column.render(rawValue, record, index));
  }

  return "";
};

/**
 * Convert array of objects to CSV format
 * @param data - Array of data objects
 * @param columns - Column definitions to determine which fields to export
 * @returns CSV string
 */
export const convertToCSV = <T extends Record<string, unknown>>(data: T[], columns: DataTableColumn<T>[]): string => {
  if (!data || data.length === 0) return "";

  const exportColumns = getExportColumns(columns);
  const headers = exportColumns.map((col) => escapeCsvCell(String(col.title)));

  const rows = data.map((item, index) =>
    exportColumns.map((col) => escapeCsvCell(getExportValue(item, col, index))).join(","),
  );

  return `\uFEFF${[headers.join(","), ...rows].join("\n")}`;
};

/**
 * Download data as a file
 * @param content - File content
 * @param filename - Filename with extension
 * @param mimeType - MIME type of the file
 */
export const downloadFile = (content: string, filename: string, mimeType: string): void => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Export data to CSV
 * @param data - Array of data objects
 * @param columns - Column definitions
 * @param filename - Filename without extension
 */
export const exportToCSV = (
  data: Record<string, unknown>[],
  columns: DataTableColumn<Record<string, unknown>>[],
  filename: string = "export",
): void => {
  const csv = convertToCSV(data, columns);
  downloadFile(csv, `${filename}.csv`, "text/csv;charset=utf-8;");
};

/**
 * Export data to Excel (CSV format, can be opened in Excel)
 * For true Excel format, would need a library like xlsx
 * @param data - Array of data objects
 * @param columns - Column definitions
 * @param filename - Filename without extension
 */
export const exportToExcel = (
  data: Record<string, unknown>[],
  columns: DataTableColumn<Record<string, unknown>>[],
  filename: string = "export",
): void => {
  const exportColumns = getExportColumns(columns);
  const headerHtml = exportColumns.map((column) => `<th>${escapeHtml(String(column.title))}</th>`).join("");
  const rowsHtml = data
    .map((item, index) => {
      const cellHtml = exportColumns
        .map((column) => `<td>${escapeHtml(getExportValue(item, column, index))}</td>`)
        .join("");
      return `<tr>${cellHtml}</tr>`;
    })
    .join("");

  const workbook = `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:x="urn:schemas-microsoft-com:office:excel"
      xmlns="http://www.w3.org/TR/REC-html40">
  <head>
    <meta charset="UTF-8" />
    <meta name="ProgId" content="Excel.Sheet" />
    <meta name="Generator" content="Codex" />
  </head>
  <body>
    <table>
      <thead>
        <tr>${headerHtml}</tr>
      </thead>
      <tbody>${rowsHtml}</tbody>
    </table>
  </body>
</html>`;

  downloadFile(workbook, `${filename}.xls`, "application/vnd.ms-excel;charset=utf-8;");
};

/**
 * Debounce function for search
 * @param func - Function to debounce
 * @param wait - Wait time in milliseconds
 * @returns Debounced function
 */
export const debounce = <F extends (...args: unknown[]) => unknown>(
  func: F,
  wait: number,
): ((...args: Parameters<F>) => void) => {
  let timeout: NodeJS.Timeout | null = null;

  return (...args: Parameters<F>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * Get nested value from object using dot notation
 * @param obj - Object to get value from
 * @param path - Path in dot notation (e.g., 'user.name')
 * @returns Value at path
 */
export const getNestedValue = (obj: Record<string, unknown>, path: string | string[]): unknown => {
  const keys = Array.isArray(path) ? path : path.split(".");
  return keys.reduce<unknown>((acc, key) => {
    if (acc && typeof acc === "object" && key in acc) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
};

/**
 * Format date for display
 * @param date - Date string or object
 * @returns Formatted date string
 */
export const formatDate = (date: string | Date): string => {
  if (!date) return "";
  const d = new Date(date);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * Format currency for display
 * @param amount - Amount to format
 * @param currency - Currency code (default: USD)
 * @returns Formatted currency string
 */
export const formatCurrency = (amount: number, currency: string = "USD"): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(amount);
};
